import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recentFinancialYears } from '../format';
import {
  getSettings,
  putSettings,
  getXeroStatus,
  getXeroConnectUrl,
  getMs365Status,
  getMs365ConnectUrl,
  REMINDER_OPTIONS,
  type OrgSettings,
  type ReminderInterval,
  type ScanTime,
  type XeroStatus,
  type Ms365Status,
} from '../api/settings';
import { UnauthorizedError } from '../api/client';
import { ErrorBanner, toMessage } from '../components/common';

// Example due-date hint for a reminder interval, measured from today: a weekday
// name if it lands within the coming week, else DD-MMM-YYYY.
function reminderHint(interval: ReminderInterval): string {
  const d = new Date();
  const addBusiness = (n: number) => {
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
  };
  if (interval === '2bd') addBusiness(2);
  else if (interval === '3bd') addBusiness(3);
  else if (interval === '1w') d.setDate(d.getDate() + 7);
  else if (interval === '2w') d.setDate(d.getDate() + 14);
  else if (interval === '1m') d.setMonth(d.getMonth() + 1);
  const diffDays = Math.round((d.getTime() - Date.now()) / 864e5);
  return diffDays <= 6
    ? d.toLocaleDateString('en-AU', { weekday: 'long' })
    : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Editable preset list (engagement types / document requirements).
function PresetList({ label, items, onChange }: { label: string; items: string[]; onChange: (v: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !items.some((i) => i.toLowerCase() === v.toLowerCase())) onChange([...items, v]);
    setDraft('');
  };
  return (
    <div className="field">
      <label>{label}</label>
      <ul className="preset-list">
        {items.map((it, i) => (
          <li key={i}>
            <span>{it}</span>
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} aria-label={`Remove ${it}`}>×</button>
          </li>
        ))}
        {items.length === 0 ? <li className="muted">None yet</li> : null}
      </ul>
      <div className="preset-add">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={`Add a ${label.toLowerCase().replace(/s$/, '')}`}
        />
        <button type="button" className="btn-secondary btn-mini-2" onClick={add}>Add</button>
      </div>
    </div>
  );
}

// The single settings surface for the app (the standalone modal was retired):
// integration connections, quote follow-up reminders, document scan times, and
// the job-creation form presets. Org-wide (one SETTINGS/ORG record).
export function Settings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [xero, setXero] = useState<XeroStatus | null>(null);
  const [ms365, setMs365] = useState<Ms365Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettings()
      .then(setSettings)
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          navigate('/signin', { replace: true });
          return;
        }
        setError(toMessage(e));
      });
    getXeroStatus()
      .then(setXero)
      .catch(() => setXero({ connected: false, tenantName: null }));
    getMs365Status()
      .then(setMs365)
      .catch(() => setMs365({ connected: false, mailbox: null }));
  }, [navigate]);

  async function connectMs365() {
    try {
      const res = await getMs365ConnectUrl();
      const url = res.url || res.authorizeUrl;
      if (url) window.location.assign(url);
      else setError('Microsoft 365 did not return an authorization URL. Add the app credentials first.');
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        navigate('/signin', { replace: true });
        return;
      }
      setError(toMessage(e));
    }
  }

  function patch(p: Partial<OrgSettings>) {
    setSettings((s) => (s ? { ...s, ...p } : s));
  }

  async function connectXero() {
    try {
      const res = await getXeroConnectUrl();
      const url = res.url || res.authorizeUrl;
      if (url) window.location.assign(url);
      else setError('Xero did not return an authorization URL.');
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        navigate('/signin', { replace: true });
        return;
      }
      setError(toMessage(e));
    }
  }

  async function save() {
    if (!settings) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await putSettings(settings);
      setSettings(saved);
      setNotice('Settings saved.');
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const addReminder = () => {
    if (!settings) return;
    const used = new Set(settings.reminders);
    const next = REMINDER_OPTIONS.find((o) => !used.has(o.value));
    if (next && settings.reminders.length < 4) patch({ reminders: [...settings.reminders, next.value] });
  };
  const addScan = () => {
    if (!settings || settings.scanTimes.length >= 4) return;
    patch({ scanTimes: [...settings.scanTimes, { hour: 9, minute: 0, ampm: 'AM' }] });
  };

  return (
    <div className="settings-page">
      <div className="page-head">
        <button className="btn-secondary btn-back" onClick={() => navigate('/jobs')}>
          ← Back to valuations
        </button>
      </div>
      <h1>Settings</h1>
      <p className="muted">Integrations, reminders, and job-form presets for the automation pipeline.</p>

      {error ? <ErrorBanner message={error} /> : null}
      {notice ? <div className="notice">{notice}</div> : null}

      {!settings ? (
        <p className="muted">Loading settings.</p>
      ) : (
        <>
          {/* Connections */}
          <section className="card settings-section">
            <h3>Connections</h3>
            <div className="conn-row">
              <span className="conn-name">Xero</span>
              <span className={`conn-state ${xero?.connected ? 'ok' : 'off'}`}>
                {xero == null ? 'Checking.' : xero.connected ? `Connected: ${xero.tenantName ?? 'organisation'}` : 'Not connected'}
              </span>
              <button className="btn-secondary btn-mini-2" onClick={connectXero} disabled={xero == null}>
                {xero?.connected ? 'Reconnect' : 'Connect'}
              </button>
            </div>
            <div className="conn-row">
              <span className="conn-name">Microsoft 365</span>
              <span className={`conn-state ${ms365?.connected ? 'ok' : 'off'}`}>
                {ms365 == null
                  ? 'Checking.'
                  : ms365.connected
                    ? `Connected: ${ms365.mailbox ?? 'mailbox'}`
                    : 'Not connected'}
              </span>
              <button className="btn-secondary btn-mini-2" onClick={connectMs365} disabled={ms365 == null}>
                {ms365?.connected ? 'Reconnect' : 'Connect'}
              </button>
            </div>
            {ms365?.connected ? (
              <div className="conn-row" style={{ marginTop: '0.15rem' }}>
                <span className="conn-name" style={{ fontWeight: 400, fontSize: '0.85rem' }}>
                  Sends client email as
                </span>
                <span className={`conn-state ${ms365.sendAsVerified ? 'ok' : 'off'}`}>
                  {ms365.sendAs ?? 'not resolved'}
                  {ms365.sendAsVerified ? ' (verified)' : ' (NOT VERIFIED)'}
                </span>
              </div>
            ) : null}
            {/* The recorded pair. Shown whether or not they have connected, so the
                right account to sign in with is known BEFORE a wrong sign-in, and
                so a drift between what was configured and what is live is visible
                rather than silent. */}
            {ms365?.assignedSendAs ? (
              <p
                className={
                  ms365.connected && ms365.sendAs && ms365.sendAs !== ms365.assignedSendAs
                    ? 'field-error'
                    : 'muted'
                }
                style={{ marginTop: '0.4rem', fontSize: '0.8rem' }}
              >
                {ms365.connected && ms365.sendAs && ms365.sendAs !== ms365.assignedSendAs
                  ? `This account is set up to send as ${ms365.assignedSendAs}, but it is currently sending as ${ms365.sendAs}. Press Reconnect.`
                  : `Set up to connect as ${ms365.assignedLogin} and send as ${ms365.assignedSendAs}.`}
              </p>
            ) : null}
            <p className="muted" style={{ marginTop: '0.4rem', fontSize: '0.8rem' }}>
              {ms365?.connected && ms365.sendAsVerified
                ? `Signed in as ${ms365.mailbox}. Client email is sent from ${ms365.sendAs}, and a copy appears in that mailbox's Sent Items. Replies to it are read back onto the job automatically.`
                : ms365?.connected
                  ? `Signed in as ${ms365.mailbox}, but the app could not confirm access to a shared mailbox to send from. Client email will fail until this is resolved. ${ms365.sendAsError ?? ''}`
                  : 'Connect a Microsoft 365 account so the app can send client email from your practice mailbox.'}
            </p>
          </section>

          {/* Quote follow-up reminders */}
          <section className="card settings-section">
            <h3>Quote follow-up reminders (AWST)</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: '0.85rem' }}>
              Automatically email the client a nudge to sign, at each interval after the quote is sent. Up to 4, no duplicates.
            </p>
            {settings.reminders.map((r, i) => (
              <div key={i} className="settings-row">
                <select
                  value={r}
                  onChange={(e) => patch({ reminders: settings.reminders.map((x, idx) => (idx === i ? (e.target.value as ReminderInterval) : x)) })}
                >
                  {REMINDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} disabled={settings.reminders.includes(o.value) && o.value !== r}>{o.label}</option>
                  ))}
                </select>
                <span className="row-hint">(≈ {reminderHint(r)})</span>
                <button type="button" className="row-del" onClick={() => patch({ reminders: settings.reminders.filter((_, idx) => idx !== i) })}>×</button>
              </div>
            ))}
            {settings.reminders.length < 4 ? (
              <button type="button" className="btn-secondary btn-mini-2" onClick={addReminder}>+ Add reminder</button>
            ) : null}
          </section>

          {/* Chasing the client for documents */}
          <section className="card settings-section">
            <h3>Chasing outstanding documents</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: '0.85rem' }}>
              When a client has not sent everything that was requested, the app emails
              them the outstanding list on its own. It stops after the number below and
              tells the job owner instead, so an unresponsive client becomes a phone call
              rather than a polite email every few days forever.
            </p>
            <div className="form-row" style={{ gap: '1.2rem', flexWrap: 'wrap' }}>
              <label className="field" style={{ maxWidth: '15rem' }}>
                Chases before the owner is told
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={settings.docChaseMax}
                  onChange={(e) =>
                    setSettings({ ...settings, docChaseMax: Number(e.target.value) })
                  }
                />
                <span className="field-hint">0 turns chasing off entirely.</span>
              </label>
              <label className="field" style={{ maxWidth: '15rem' }}>
                Days between chases
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={settings.docChaseEveryDays}
                  onChange={(e) =>
                    setSettings({ ...settings, docChaseEveryDays: Number(e.target.value) })
                  }
                />
                <span className="field-hint">
                  {settings.docChaseMax > 0
                    ? `The owner hears about it after about ${settings.docChaseMax * settings.docChaseEveryDays} days.`
                    : 'Chasing is off, so the owner is never notified automatically.'}
                </span>
              </label>
            </div>
          </section>

          {/* Document scan times */}
          <section className="card settings-section">
            <h3>Document scan times (AWST)</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: '0.85rem' }}>
              A client upload is picked up as soon as it has been read, so these are a backstop rather than the main path: the app also sweeps every 15 minutes for anything a parse missed. Up to 4 per day.
            </p>
            {settings.scanTimes.map((t, i) => (
              <div key={i} className="settings-row">
                <input type="number" min={1} max={12} value={t.hour} style={{ width: 56 }}
                  onChange={(e) => patch({ scanTimes: settings.scanTimes.map((x, idx) => (idx === i ? { ...x, hour: Math.min(12, Math.max(1, Number(e.target.value) || 1)) } : x)) })} />
                <span>:</span>
                <input type="number" min={0} max={59} value={String(t.minute).padStart(2, '0')} style={{ width: 56 }}
                  onChange={(e) => patch({ scanTimes: settings.scanTimes.map((x, idx) => (idx === i ? { ...x, minute: Math.min(59, Math.max(0, Number(e.target.value) || 0)) } : x)) })} />
                <select value={t.ampm}
                  onChange={(e) => patch({ scanTimes: settings.scanTimes.map((x, idx) => (idx === i ? { ...x, ampm: e.target.value as ScanTime['ampm'] } : x)) })}>
                  <option>AM</option>
                  <option>PM</option>
                </select>
                <button type="button" className="row-del" onClick={() => patch({ scanTimes: settings.scanTimes.filter((_, idx) => idx !== i) })}>×</button>
              </div>
            ))}
            {settings.scanTimes.length < 4 ? (
              <button type="button" className="btn-secondary btn-mini-2" onClick={addScan}>+ Add scan time</button>
            ) : null}
          </section>

          {/* Job creation form presets */}
          <section className="card settings-section">
            <h3>Job creation form</h3>
            <p className="muted" style={{ marginTop: 0, fontSize: '0.85rem' }}>
              Preset options offered on the New job form. Removing one only affects the form; existing jobs keep their values.
            </p>
            <PresetList label="Engagement types" items={settings.engagementTypes} onChange={(v) => patch({ engagementTypes: v })} />
            <PresetList label="Document requirements" items={settings.docRequirements} onChange={(v) => patch({ docRequirements: v })} />

            {/*
              Shown as the same four years the new-job form offers, stored as offsets
              from the newest. A literal "FY24" saved today would be wrong next July,
              and a preference that silently rots is worse than no preference.
            */}
            <div className="preset-block">
              <h4>Financial years ticked by default</h4>
              <p className="field-hint">
                The New job form always offers the four most recent completed financial
                years. Choose which of them start ticked. The list rolls forward on its
                own each July.
              </p>
              {recentFinancialYears().map((fy, i, all) => {
                const offset = all.length - 1 - i;
                const on = (settings.financialYearOffsets ?? [1, 2, 3]).includes(offset);
                return (
                  <label className="tickbox" key={fy}>
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(e) => {
                        const cur = new Set(settings.financialYearOffsets ?? [1, 2, 3]);
                        if (e.target.checked) cur.add(offset);
                        else cur.delete(offset);
                        patch({ financialYearOffsets: [...cur].sort((a, b) => a - b) });
                      }}
                    />
                    <span>
                      {fy}
                      {offset === 0 ? (
                        <span className="muted"> (most recent; statements are often not ready yet)</span>
                      ) : null}
                    </span>
                  </label>
                );
              })}
              {(settings.financialYearOffsets ?? []).length === 0 ? (
                <p className="field-error">Select at least one, or every new job starts with no years.</p>
              ) : null}
            </div>
          </section>

          <div className="btn-row" style={{ marginTop: '0.5rem' }}>
            <button onClick={save} disabled={saving}>{saving ? 'Saving.' : 'Save settings'}</button>
          </div>
        </>
      )}
    </div>
  );
}
